/**
 * System Administration & Onboarding Portal
 * Main Application Component
 *
 * Routing:
 *   /              → PublicLandingView  (public — no auth required)
 *   /legal/*       → Privacy, terms, SMS opt-in (public)
 *   /i/:token      → Public invoice SMS short link
 *   /e/:token      → Public estimate SMS short link
 *   /admin/*       → AdminLayout + protected views (super_admin only)
 *
 * NOTE: Nested <Routes> inside /admin/* must use relative paths (no leading /)
 * per React Router v6 semantics.
 */

import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import AdminLayout from './components/layout/AdminLayout';
import LoginView from './components/views/LoginView';
import DashboardView from './components/views/DashboardView';
import TenantsView from './components/views/TenantsView';
import TenantDetailView from './components/views/TenantDetailView';
import OnboardingWizard from './components/views/OnboardingWizard';
import ReportsView from './components/views/ReportsView';
import ReportAuthoringView from './components/views/ReportAuthoringView';
import AllReportsView from './components/views/AllReportsView';
import SystemSettingsView from './components/views/SystemSettingsView';
import SubscriptionPlansView from './components/views/SubscriptionPlansView';
import HelpView from './components/views/HelpView';
import PublicLandingView from './components/views/PublicLandingView';
import LegalPageView from './components/views/LegalPageView';
import PublicSmsLinkRedirect from './components/views/PublicSmsLinkRedirect';
import SignupRequestsView from './components/views/SignupRequestsView';
import EmailTemplatesView from './components/views/EmailTemplatesView';
import ComplianceDocumentsView from './components/views/ComplianceDocumentsView';
import RoadmapView from './components/views/RoadmapView';
import GithubScorecardView from './components/views/GithubScorecardView';
import ComplianceReportsView from './components/views/ComplianceReportsView';
import { authService } from './services/api';
import {
  forceSessionExpired,
  shouldBlockSilentSso,
  SESSION_REASON,
} from './services/sessionExpiry';
import { canAccessOnboardingAdmin } from './utils/roleAccess';

function AdminPortal({ user, onLogin, onLogout }) {
  if (!user) {
    return <LoginView onLogin={onLogin} />;
  }
  return (
    <AdminLayout user={user} onLogout={onLogout}>
      <Routes>
        {/* Relative paths — matched against the remainder after /admin/ */}
        <Route index element={<DashboardView />} />
        <Route path="tenants" element={<TenantsView />} />
        <Route path="tenants/:id" element={<TenantDetailView />} />
        <Route path="signup-requests" element={<SignupRequestsView />} />
        <Route path="email-templates" element={<EmailTemplatesView />} />
        <Route path="compliance-documents" element={<ComplianceDocumentsView />} />
        <Route path="onboarding" element={<OnboardingWizard />} />
        <Route path="reports" element={<ReportsView />} />
        <Route path="reports/new" element={<ReportAuthoringView />} />
        <Route path="reports/:id" element={<ReportAuthoringView />} />
        <Route path="all-reports" element={<AllReportsView />} />
        <Route path="roadmap" element={<RoadmapView />} />
        <Route path="github-scorecard" element={<GithubScorecardView />} />
        <Route path="compliance-reports" element={<ComplianceReportsView />} />
        <Route path="settings" element={<SystemSettingsView />} />
        <Route path="subscription-plans" element={<SubscriptionPlansView />} />
        <Route path="help" element={<HelpView appSlug="onboarding" />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Routes>
    </AdminLayout>
  );
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const checkAuth = async () => {
      if (shouldBlockSilentSso()) {
        localStorage.removeItem('admin_token');
        setUser(null);
        setLoading(false);
        return;
      }

      const token = localStorage.getItem('admin_token');
      if (token) {
        try {
          const response = await authService.getProfile();
          const userData = response.data || response;
          if (canAccessOnboardingAdmin(userData)) {
            setUser(userData);
          } else {
            localStorage.removeItem('admin_token');
          }
        } catch (err) {
          if (err?.response?.status === 401) {
            forceSessionExpired(SESSION_REASON.EXPIRED, err.response?.data?.message);
          } else {
            localStorage.removeItem('admin_token');
          }
        }
      }
      setLoading(false);
    };
    checkAuth();
  }, []);

  const handleLogin = (userData, token) => {
    localStorage.setItem('admin_token', token);
    setUser(userData);
  };

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    setUser(null);
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <BrowserRouter>
      <Routes>
        {/* Public landing — always visible, no auth check */}
        <Route path="/" element={<PublicLandingView />} />
        <Route path="/legal/privacy" element={<LegalPageView />} />
        <Route path="/legal/terms" element={<LegalPageView />} />
        <Route path="/legal/sms-opt-in" element={<LegalPageView />} />
        <Route path="/legal/data-deletion" element={<LegalPageView />} />
        <Route path="/i/:token" element={<PublicSmsLinkRedirect kind="invoice" />} />
        <Route path="/e/:token" element={<PublicSmsLinkRedirect kind="estimate" />} />

        {/* Admin portal — login wall inside AdminPortal component */}
        <Route
          path="/admin/*"
          element={
            <AdminPortal
              user={user}
              onLogin={handleLogin}
              onLogout={handleLogout}
            />
          }
        />

        {/* Everything else → public landing */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
